import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { CreateLessonRequest, ParsedError } from "../../interfaces";
import {
  useGetLessonQuery,
  useAddLessonMutation,
  useUpdateLessonMutation,
} from "../../store/api/lessonApi";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../enums";
import { ActivityButton, SequenceNumberInput } from "../../components/Common";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  chapterId: string;
};
export const AddEditLessonModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  chapterId,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateLessonRequest>();

  const {
    data: lesson,
    error: getLessonError,
    isLoading: isLoadingLesson,
  } = useGetLessonQuery(id as string, {
    skip: !id,
  });

  const [
    addLesson,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddLessonMutation();

  const [
    updateLesson,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateLessonMutation();

  //#region form
  const form = useFormik<CreateLessonRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      chapterId: request?.chapterId || chapterId,
      sequenceNumber: request?.sequenceNumber || 0,
      enableInFlashcard: request?.enableInFlashcard || false,
      enableInLecture: request?.enableInLecture || true,
      enableInQuestion: request?.enableInQuestion || false,
      isFree: request?.isFree || false,
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      chapterId: Yup.string(),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
      enableInFlashcard: Yup.bool(),
      enableInLecture: Yup.bool(),
      enableInQuestion: Yup.bool(),
      isFree: Yup.bool(),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateLesson({
            id,
            data: { ...values, sequenceNumber: Number(values.sequenceNumber) },
          }).unwrap();
        } else {
          await addLesson({
            ...values,
            sequenceNumber: Number(values.sequenceNumber),
          }).unwrap();
        }
        toast.success(t("savedSuccessfully"), { autoClose: 3000 });
        onClose();
      } catch (err) {}
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  useEffect(() => {
    if (id && lesson) {
      form.setValues({
        name: lesson.name,
        chapterId: lesson.chapterId,
        sequenceNumber: lesson?.sequenceNumber || 0,
        enableInFlashcard: lesson?.enableInFlashcard,
        enableInLecture: lesson?.enableInLecture,
        enableInQuestion: lesson?.enableInQuestion,
        isFree: lesson?.isFree,
      });
    }
  }, [lesson, isLoadingLesson, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  return (
    <Modal
      id="addEdit"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {id
            ? t("editItem", { item: "lesson" })
            : t("addItem", { item: "lesson" })}
        </ModalHeader>
        <ModalBody>
          <SequenceNumberInput
            tabIndex={1}
            value={form.values.sequenceNumber}
            onChange={(e) => {
              e.preventDefault();
              const { value } = e.target;
              const regex =
                /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
              if (regex.test(value.toString())) {
                form.setFieldValue("sequenceNumber", value);
              } else {
                form.setFieldValue("sequenceNumber", "");
              }
            }}
            onBlur={form.handleBlur}
            invalid={
              form.touched.sequenceNumber && form.errors.sequenceNumber
                ? true
                : false
            }
            sequenceType={SequenceEnum.LESSON}
            onGenerateSequenceNumber={(v: number) => {
              form.setFieldValue("sequenceNumber", v);
            }}
            mode={id ? "edit" : "add"}
            request={{
              type: SequenceEnum.LESSON,
              chapterId: chapterId,
            }}
          />
          {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
            <FormFeedback type="invalid">
              {form.errors.sequenceNumber as string}
            </FormFeedback>
          ) : null}
          <div className="mb-3">
            <label htmlFor="name.en" className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={!!getLessonError}
              autoComplete="off"
              id="name.en"
              name="name.en"
              value={form.values?.name.en}
              type="text"
              className="form-control"
              placeholder={t("enterInEnglish")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.en && form.errors.name?.en ? true : false
              }
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.ar" className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              disabled={!!getLessonError}
              autoComplete="off"
              auto
              id="name.ar"
              name="name.ar"
              value={form.values.name?.ar}
              type="text"
              className="form-control"
              placeholder={t("enterInArabic")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.ar && form.errors.name?.ar ? true : false
              }
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Col lg={4}>
              <div>
                <div className="form-check mb-4 mt-2">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    id="enableInLecture"
                    checked={form.values.enableInLecture}
                    value={(form.values.enableInLecture || false) as any}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                  />
                  <Label className="form-check-label" htmlFor="enableInLecture">
                    {t("enableInLecture")}
                  </Label>
                </div>
              </div>
            </Col>
          </div>
          <div className="mb-3">
            <Col lg={4}>
              <div>
                <div className="form-check mb-4 mt-2">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    id="enableInFlashcard"
                    checked={form.values.enableInFlashcard}
                    value={(form.values.enableInFlashcard || false) as any}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="enableInFlashcard"
                  >
                    {t("enableInFlashcard")}
                  </Label>
                </div>
              </div>
            </Col>
          </div>
          <div className="mb-3">
            <Col lg={4}>
              <div>
                <div className="form-check mb-4 mt-2">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    id="enableInQuestion"
                    checked={form.values.enableInQuestion}
                    value={(form.values.enableInQuestion || false) as any}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="enableInQuestion"
                  >
                    {t("enableInQuestion")}
                  </Label>
                </div>
              </div>
            </Col>
          </div>
          <div className="mb-3">
            <Col lg={4}>
              <div>
                <div className="form-check mb-4 mt-2">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    id="isFree"
                    checked={form.values.isFree}
                    value={(form.values.isFree || false) as any}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                  />
                  <Label className="form-check-label" htmlFor="isFree">
                    {t("markAsFreeLesson")}
                  </Label>
                </div>
              </div>
            </Col>
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isLoadingAdd || isLoadingUpdate}
            disabled={isLoadingAdd || isLoadingUpdate || !!getLessonError}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
